import { useState, useMemo } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Scissors, Star, Zap, Crown, CheckCircle2, Loader2, Sparkles, ArrowRight,
  Building2, CreditCard, MessageCircle, Instagram, Phone, ChevronRight, Wifi,
  Bot, Calendar, Users, Settings, ExternalLink, Eye, EyeOff, LockKeyhole,
  Mail, UserRound, ShieldCheck, LogIn
} from "lucide-react";
import { toast } from "sonner";
import { useLocation } from "wouter";

const PLAN_ICONS: Record<string, React.ReactNode> = {
  basic: <Scissors className="w-6 h-6" />,
  professional: <Star className="w-6 h-6" />,
  premium: <Crown className="w-6 h-6" />,
  elite: <Sparkles className="w-6 h-6" />,
};

const PLAN_COLORS: Record<string, string> = {
  basic: "border-border hover:border-primary/50",
  professional: "border-primary bg-primary/5",
  premium: "border-amber-400 bg-amber-50/50",
  elite: "border-yellow-500/60 bg-gradient-to-b from-yellow-500/5 to-amber-900/5 shadow-lg shadow-yellow-500/10",
};

type Step = "plan" | "details" | "payment" | "channels" | "agent" | "setup" | "success";

const STEPS: { id: Step; label: string }[] = [
  { id: "plan", label: "Plano" },
  { id: "details", label: "Dados" },
  { id: "payment", label: "Pagamento" },
  { id: "channels", label: "Canais" },
  { id: "agent", label: "Agente IA" },
  { id: "setup", label: "Configuração" },
];

export default function OnboardingPage() {
  const { user, isAuthenticated, loading, refresh } = useAuth();
  const [, navigate] = useLocation();
  const [step, setStep] = useState<Step>("plan");
  const [selectedPlan, setSelectedPlan] = useState<number | null>(null);
  const [salonId, setSalonId] = useState<number | null>(null);
  const [channelRequested, setChannelRequested] = useState<"whatsapp" | "instagram" | null>(null);
  const [channelPhone, setChannelPhone] = useState("");
  const [contractAccepted, setContractAccepted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    slug: "",
    description: "",
    address: "",
    phone: "",
    email: "",
  });
  const [accountForm, setAccountForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [showAccountPassword, setShowAccountPassword] = useState(false);

  const { data: plans, isLoading: plansLoading } = trpc.plans.list.useQuery();
  const { data: mySalon } = trpc.salon.getMySalon.useQuery(undefined, { enabled: isAuthenticated });
  const activeSalonId = salonId ?? mySalon?.id ?? null;
  const onboardingSalonInput = useMemo(() => ({ salonId: activeSalonId ?? 0 }), [activeSalonId]);
  const { data: onboardingServices } = trpc.services.list.useQuery(onboardingSalonInput, { enabled: !!activeSalonId });
  const { data: onboardingProfessionals } = trpc.professionals.list.useQuery(onboardingSalonInput, { enabled: !!activeSalonId });
  const { data: onboardingSchedule } = trpc.salon.getWorkingSchedule.useQuery(undefined, { enabled: !!activeSalonId });
  const { data: onboardingChannels } = trpc.salon.getChannelRequests.useQuery(undefined, { enabled: !!activeSalonId });

  const registerSalon = trpc.salon.register.useMutation({
    onSuccess: (data) => {
      setSalonId((data as any)?.salonId ?? null);
      setStep("payment");
      toast.success("Salão cadastrado! Agora escolha como pagar.");
    },
    onError: (e) => toast.error(e.message),
  });

  const registerSalonOwner = trpc.auth.registerSalonOwner.useMutation({
    onSuccess: async () => {
      window.localStorage.setItem("bb_access_mode", "admin");
      await refresh();
      toast.success("Conta criada! Agora escolha o plano para o seu salão.");
    },
    onError: (error) => toast.error(error.message),
  });

  const createCheckout = trpc.salon.createSubscriptionCheckout.useMutation({
    onSuccess: (data) => {
      if (data.url) {
        toast.info("Redirecionando para o pagamento...");
        window.open(data.url, "_blank");
        // Avança para canais após abrir o checkout (o usuário pode voltar)
        setTimeout(() => setStep("channels"), 1500);
      }
    },
    onError: (e) => toast.error(e.message),
  });

  const requestChannel = trpc.salon.requestChannelConnection.useMutation({
    onSuccess: () => {
      setStep("success");
      toast.success("Solicitação enviada! Nossa equipe entrará em contato.");
    },
    onError: (e) => toast.error(e.message),
  });

  // Auto-generate slug from name
  const handleNameChange = (name: string) => {
    const slug = name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
    setForm((f) => ({ ...f, name, slug }));
  };

  const handleSubmitDetails = () => {
    if (!selectedPlan) return;
    if (!form.name.trim()) { toast.error("Nome do salão é obrigatório."); return; }
    if (!form.slug.trim()) { toast.error("URL do salão é obrigatória."); return; }
    if (!contractAccepted) { toast.error("Você precisa aceitar o Contrato de Licença para continuar."); return; }
    registerSalon.mutate({ ...form, planId: selectedPlan, contractAccepted: true });
  };

  const handleCreateAccount = (event: React.FormEvent) => {
    event.preventDefault();
    if (!accountForm.name.trim() || !accountForm.email.trim() || !accountForm.password) {
      toast.error("Preencha seu nome, e-mail e senha para continuar.");
      return;
    }
    if (accountForm.password !== accountForm.confirmPassword) {
      toast.error("As senhas não conferem.");
      return;
    }
    registerSalonOwner.mutate({
      name: accountForm.name,
      email: accountForm.email,
      password: accountForm.password,
    });
  };

  const handlePayment = () => {
    if (!selectedPlan) return;
    createCheckout.mutate({ planId: selectedPlan, origin: window.location.origin });
  };

  const handleChannelRequest = () => {
    if (!channelRequested) {
      setStep("agent");
      return;
    }
    requestChannel.mutate({
      channelType: channelRequested,
      contactPhone: channelPhone || undefined,
      contactName: user?.name || undefined,
    }, {
      onSuccess: () => setStep("agent"),
    });
  };

  const stepIndex = STEPS.findIndex(s => s.id === step);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-amber-50/60 to-orange-100/70 px-4 py-7 sm:py-10">
        <div className="mx-auto grid min-h-[calc(100vh-3.5rem)] max-w-5xl items-center gap-7 lg:grid-cols-[1fr_460px]">
          <section className="mx-auto max-w-xl text-center lg:mx-0 lg:text-left">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-amber-200 bg-white/80 px-3 py-1.5 text-xs font-semibold text-amber-900 shadow-sm">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              Cadastro simples e seguro
            </div>
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-lg shadow-primary/25 lg:mx-0">
              <Scissors className="h-8 w-8" />
            </div>
            <h1 className="text-4xl font-medium leading-tight text-foreground sm:text-5xl" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              Comece o cadastro do seu salão
            </h1>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
              Crie seu acesso com e-mail e senha. Você não precisa de uma conta Manus e pode concluir tudo diretamente pelo Instagram ou pelo celular.
            </p>
            <div className="mt-7 grid gap-3 text-left sm:grid-cols-3 lg:grid-cols-1">
              {[
                ["1", "Crie seu acesso", "Use o e-mail do responsável pelo salão."],
                ["2", "Escolha o plano", "Todos começam com 15 dias gratuitos."],
                ["3", "Cadastre a operação", "Serviços, equipe e agenda em etapas guiadas."],
              ].map(([number, title, description]) => (
                <div key={number} className="flex items-start gap-3 rounded-xl border border-amber-100 bg-white/70 p-3.5 shadow-sm">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">{number}</span>
                  <span>
                    <strong className="block text-sm text-foreground">{title}</strong>
                    <span className="block pt-0.5 text-xs leading-relaxed text-muted-foreground">{description}</span>
                  </span>
                </div>
              ))}
            </div>
          </section>

          <Card className="border-amber-100 bg-white/95 shadow-xl shadow-amber-950/10 backdrop-blur">
            <CardHeader className="pb-3 text-center">
              <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Building2 className="h-5 w-5" />
              </div>
              <p className="text-xs font-semibold uppercase tracking-wider text-primary">Etapa 1 de 3</p>
              <CardTitle className="mt-1 text-2xl">Crie o acesso do responsável</CardTitle>
              <CardDescription>Este acesso será usado para entrar e administrar seu salão.</CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-4" onSubmit={handleCreateAccount}>
                <div className="space-y-1.5">
                  <Label htmlFor="owner-name">Seu nome</Label>
                  <div className="relative">
                    <UserRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input id="owner-name" value={accountForm.name} onChange={(event) => setAccountForm((current) => ({ ...current, name: event.target.value }))} placeholder="Nome do responsável" autoComplete="name" className="h-11 pl-9" autoFocus />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="owner-email">E-mail</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input id="owner-email" type="email" value={accountForm.email} onChange={(event) => setAccountForm((current) => ({ ...current, email: event.target.value }))} placeholder="voce@exemplo.com" autoComplete="email" inputMode="email" className="h-11 pl-9" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="owner-password">Crie uma senha</Label>
                  <div className="relative">
                    <LockKeyhole className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input id="owner-password" type={showAccountPassword ? "text" : "password"} value={accountForm.password} onChange={(event) => setAccountForm((current) => ({ ...current, password: event.target.value }))} placeholder="Pelo menos 8 caracteres" autoComplete="new-password" className="h-11 px-9" />
                    <button type="button" onClick={() => setShowAccountPassword((visible) => !visible)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label={showAccountPassword ? "Ocultar senha" : "Mostrar senha"}>
                      {showAccountPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">Use ao menos uma letra e um número.</p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="owner-password-confirm">Confirme a senha</Label>
                  <Input id="owner-password-confirm" type={showAccountPassword ? "text" : "password"} value={accountForm.confirmPassword} onChange={(event) => setAccountForm((current) => ({ ...current, confirmPassword: event.target.value }))} placeholder="Digite a mesma senha" autoComplete="new-password" className="h-11" />
                </div>
                <Button type="submit" className="h-11 w-full bg-primary text-primary-foreground shadow-md shadow-primary/20" disabled={registerSalonOwner.isPending}>
                  {registerSalonOwner.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Criando acesso...</> : <>Continuar cadastro <ArrowRight className="ml-2 h-4 w-4" /></>}
                </Button>
              </form>
              <div className="mt-5 border-t border-border pt-4 text-center">
                <p className="text-sm text-muted-foreground">Já possui conta?</p>
                <Button variant="link" className="mt-0.5 h-auto px-0 font-semibold text-primary" onClick={() => navigate("/login")}>
                  <LogIn className="mr-1.5 h-4 w-4" /> Entrar no sistema
                </Button>
              </div>
              <div className="mt-4 flex items-start gap-2 rounded-lg bg-muted/60 p-3 text-xs leading-relaxed text-muted-foreground">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>Seu acesso é protegido por senha. Você poderá redefini-la pelo e-mail caso necessário.</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (mySalon && step === "plan") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-3xl font-light mb-3" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            Você já tem um salão!
          </h1>
          <p className="text-muted-foreground mb-6">
            Seu salão <strong>{mySalon.name}</strong> já está cadastrado na plataforma.
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
            <Button onClick={() => { setSalonId(mySalon.id); setStep("setup"); }} className="bg-primary text-primary-foreground">
              Continuar configuração <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Button variant="outline" onClick={() => navigate("/admin")}>Ir para o Painel Admin</Button>
          </div>
        </div>
      </div>
    );
  }

  if (step === "success") {
    return (
      <div className="min-h-screen bg-background">
        <nav className="border-b border-border/50 bg-white/90 backdrop-blur-md sticky top-0 z-10">
          <div className="container flex items-center h-16">
            <div className="flex items-center gap-2">
              <Scissors className="w-5 h-5 text-primary" />
              <span className="font-semibold text-foreground" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem" }}>Beleza &amp; Bronze</span>
            </div>
          </div>
        </nav>
        <div className="container py-12 max-w-2xl">
          <div className="text-center mb-10">
            <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="text-4xl font-light mb-3" style={{ fontFamily: "Cormorant Garamond, serif" }}>Cadastro concluído!</h1>
            <p className="text-muted-foreground">
              Seu salão está ativo com <strong>15 dias de trial gratuito</strong>.
              {channelRequested && <> Nossa equipe entrará em contato para configurar seu {channelRequested === "whatsapp" ? "WhatsApp" : "Instagram"}.</>}
            </p>
          </div>

          {/* Checklist de próximos passos */}
          <div className="space-y-3 mb-8">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">Próximos passos para começar</h2>
            {[
              {
                icon: <Scissors className="w-5 h-5 text-primary" />,
                title: "Cadastre seus serviços",
                desc: "Adicione corte, coloração, manicure e outros serviços com preços e durações.",
                href: "/admin/servicos",
                cta: "Adicionar serviços",
              },
              {
                icon: <Users className="w-5 h-5 text-blue-500" />,
                title: "Adicione seus profissionais",
                desc: "Cadastre a equipe com horários de trabalho e serviços que cada um realiza.",
                href: "/admin/profissionais",
                cta: "Adicionar profissionais",
              },
              {
                icon: <Calendar className="w-5 h-5 text-green-500" />,
                title: "Configure a agenda",
                desc: "Defina horários de funcionamento, intervalos e regras de agendamento.",
                href: "/admin/configuracoes",
                cta: "Configurar agenda",
              },
              {
                icon: <Bot className="w-5 h-5 text-amber-500" />,
                title: "Ative o Agente de IA",
                desc: "Solicite a ativação do assistente virtual que responde clientes no WhatsApp 24/7.",
                href: "/admin",
                cta: "Ver painel",
                badge: "Requer configuração do Super Admin",
              },
              {
                icon: <ExternalLink className="w-5 h-5 text-purple-500" />,
                title: "Compartilhe seu link de agendamento",
                desc: "Envie o link da sua página de agendamento para clientes e redes sociais.",
                href: "/admin",
                cta: "Ver link",
              },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 p-4 rounded-xl border border-border/60 bg-card hover:border-primary/30 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                  {item.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm">{item.title}</p>
                    {item.badge && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">{item.badge}</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
                </div>
                <a href={item.href}>
                  <Button variant="outline" size="sm" className="shrink-0 text-xs">{item.cta}</Button>
                </a>
              </div>
            ))}
          </div>

          <Button onClick={() => navigate("/admin")} className="bg-primary text-primary-foreground w-full" size="lg">
            Acessar Painel Admin <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="border-b border-border/50 bg-white/90 backdrop-blur-md sticky top-0 z-10">
        <div className="container flex items-center justify-between h-16">
          <div className="flex items-center gap-2">
            <Scissors className="w-5 h-5 text-primary" />
            <span className="font-semibold text-foreground" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem" }}>
              Beleza & Bronze
            </span>
            <Badge variant="secondary" className="text-xs">Plataforma</Badge>
          </div>
          <span className="text-sm text-muted-foreground hidden sm:block">Olá, {user?.name}</span>
        </div>
      </nav>

      <div className="container py-10 max-w-4xl">
        {/* Progress Steps */}
        <div className="flex items-center gap-1 mb-10 overflow-x-auto pb-2">
          {STEPS.map((s, i) => {
            const isCompleted = stepIndex > i;
            const isCurrent = step === s.id;
            return (
              <div key={s.id} className="flex items-center gap-1 shrink-0">
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all text-sm ${
                  isCurrent ? "bg-primary text-primary-foreground font-medium" :
                  isCompleted ? "bg-green-500/15 text-green-700" :
                  "bg-muted text-muted-foreground"
                }`}>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                    isCurrent ? "bg-white/20" :
                    isCompleted ? "bg-green-500 text-white" :
                    "bg-muted-foreground/20"
                  }`}>
                    {isCompleted ? <CheckCircle2 className="w-3 h-3" /> : i + 1}
                  </div>
                  <span>{s.label}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <ChevronRight className="w-4 h-4 text-muted-foreground/40 shrink-0" />
                )}
              </div>
            );
          })}
        </div>

        {/* Step 1: Plano */}
        {step === "plan" && (
          <div>
            <div className="mb-8">
              <h1 className="text-4xl font-light text-foreground mb-2" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                Escolha seu Plano
              </h1>
              <p className="text-muted-foreground">15 dias grátis em todos os planos. Cancele quando quiser.</p>
            </div>

            {plansLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {(plans ?? []).filter(p => p.isActive).map((plan) => (
                  <Card
                    key={plan.id}
                    className={`cursor-pointer transition-all duration-200 ${
                      selectedPlan === plan.id
                        ? "border-primary ring-2 ring-primary/20 shadow-lg"
                        : PLAN_COLORS[plan.slug] ?? "border-border hover:border-primary/50"
                    }`}
                    onClick={() => setSelectedPlan(plan.id)}
                  >
                    <CardHeader>
                      <div className="flex items-center justify-between mb-2">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${plan.slug === 'elite' ? 'bg-yellow-500/20 text-yellow-500' : 'bg-primary/10 text-primary'}`}>
                          {PLAN_ICONS[plan.slug] ?? <Zap className="w-6 h-6" />}
                        </div>
                        <div className="flex items-center gap-2">
                          {plan.slug === "elite" && (
                            <Badge className="bg-yellow-500 text-black text-xs font-bold">Mais completo</Badge>
                          )}
                          {plan.slug === "professional" && (
                            <Badge className="bg-primary text-primary-foreground text-xs">Popular</Badge>
                          )}
                          {selectedPlan === plan.id && (
                            <CheckCircle2 className={`w-5 h-5 ${plan.slug === 'elite' ? 'text-yellow-500' : 'text-primary'}`} />
                          )}
                        </div>
                      </div>
                      <CardTitle className="text-xl font-medium">{plan.name}</CardTitle>
                      <div className="flex items-baseline gap-1">
                        <span className={`text-3xl font-light ${plan.slug === 'elite' ? 'text-yellow-500' : 'text-foreground'}`}>
                          R$ {Number(plan.priceMonthly).toFixed(0)}
                        </span>
                        <span className="text-muted-foreground text-sm">/mês</span>
                      </div>
                      {plan.description && (
                        <CardDescription className="text-sm">{plan.description}</CardDescription>
                      )}
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-center gap-2 text-muted-foreground">
                          <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                          Até {plan.maxProfessionals === 999 ? 'ilimitados' : plan.maxProfessionals} profissionais
                        </li>
                        <li className="flex items-center gap-2 text-muted-foreground">
                          <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                          Calendário e agendamentos
                        </li>
                        {plan.hasAiChatbot && (
                          <li className="flex items-center gap-2 text-muted-foreground">
                            <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                            Assistente de IA
                          </li>
                        )}
                        {plan.hasOnlinePayment && (
                          <li className="flex items-center gap-2 text-muted-foreground">
                            <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                            Pagamento online
                          </li>
                        )}
                        {(plan as any).hasPanelChamada && (
                          <li className="flex items-center gap-2 text-muted-foreground">
                            <CheckCircle2 className="w-4 h-4 text-yellow-500 shrink-0" />
                            Painel de chamada (TV)
                          </li>
                        )}
                        {(plan as any).hasMediaPromocional && (
                          <li className="flex items-center gap-2 text-muted-foreground">
                            <CheckCircle2 className="w-4 h-4 text-yellow-500 shrink-0" />
                            Mídia promocional na TV
                          </li>
                        )}
                        {(plan as any).hasAiWhatsapp && (
                          <li className="flex items-center gap-2 text-muted-foreground">
                            <CheckCircle2 className="w-4 h-4 text-yellow-500 shrink-0" />
                            IA integrada no WhatsApp
                          </li>
                        )}
                        {(plan as any).hasAiInstagram && (
                          <li className="flex items-center gap-2 text-muted-foreground">
                            <CheckCircle2 className="w-4 h-4 text-yellow-500 shrink-0" />
                            IA integrada no Instagram
                          </li>
                        )}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            <div className="flex justify-end">
              <Button
                disabled={!selectedPlan}
                onClick={() => setStep("details")}
                className="bg-primary text-primary-foreground px-8"
              >
                Continuar <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Dados do Salão */}
        {step === "details" && (
          <div>
            <div className="mb-8">
              <h1 className="text-4xl font-light text-foreground mb-2" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                Dados do Salão
              </h1>
              <p className="text-muted-foreground">Preencha as informações do seu estabelecimento.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="space-y-2">
                <Label htmlFor="name">Nome do Salão *</Label>
                <Input
                  id="name"
                  placeholder="Ex: Salão da Maria"
                  value={form.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="slug">URL do Salão *</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground whitespace-nowrap">beleza.app/</span>
                  <Input
                    id="slug"
                    placeholder="salao-da-maria"
                    value={form.slug}
                    onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") }))}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Telefone / WhatsApp</Label>
                <Input
                  id="phone"
                  placeholder="(11) 99999-9999"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">E-mail do Salão</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="contato@seusalao.com.br"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="address">Endereço</Label>
                <Input
                  id="address"
                  placeholder="Rua das Flores, 123 - São Paulo, SP"
                  value={form.address}
                  onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  placeholder="Conte um pouco sobre seu salão..."
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={3}
                />
              </div>
            </div>

            {/* Aceite do Contrato */}
            <div className="flex items-start gap-3 p-4 rounded-lg border border-border bg-muted/30">
              <input
                type="checkbox"
                id="contractAccept"
                checked={contractAccepted}
                onChange={(e) => setContractAccepted(e.target.checked)}
                className="mt-1 w-4 h-4 accent-primary cursor-pointer"
              />
              <label htmlFor="contractAccept" className="text-sm text-muted-foreground cursor-pointer leading-relaxed">
                Li e concordo com o{" "}
                <a
                  href="/contrato-saas"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline hover:text-primary/80 font-medium"
                >
                  Contrato de Licença de Uso da Plataforma (B2B)
                </a>
                {" "}da Beleza e Bronze, incluindo os termos de uso, política de privacidade e condições de serviço.
              </label>
            </div>

            <div className="flex items-center justify-between">
              <Button variant="outline" onClick={() => setStep("plan")}>
                Voltar
              </Button>
              <Button
                onClick={handleSubmitDetails}
                disabled={registerSalon.isPending || !form.name.trim() || !contractAccepted}
                className="bg-primary text-primary-foreground px-8"
              >
                {registerSalon.isPending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cadastrando...</>
                ) : (
                  <>Continuar <ArrowRight className="ml-2 w-4 h-4" /></>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Pagamento */}
        {step === "payment" && (
          <div>
            <div className="mb-8">
              <h1 className="text-4xl font-light text-foreground mb-2" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                Pagamento
              </h1>
              <p className="text-muted-foreground">Ative sua assinatura para continuar após o trial.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Card de trial */}
              <Card className="border-green-200 bg-green-50/50">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Trial Gratuito Ativo</CardTitle>
                      <CardDescription>15 dias sem cobranças</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                      Acesso completo à plataforma
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                      Sem necessidade de cartão agora
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                      Cancele antes do fim sem cobrança
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Card de assinatura */}
              <Card className="border-primary/30 bg-primary/5">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Ativar Assinatura</CardTitle>
                      <CardDescription>Configure o pagamento agora</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    Configure seu cartão agora e não se preocupe — a cobrança só começa após o trial de 15 dias.
                  </p>
                  <Button
                    onClick={handlePayment}
                    disabled={createCheckout.isPending}
                    className="w-full bg-primary text-primary-foreground"
                  >
                    {createCheckout.isPending ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Abrindo...</>
                    ) : (
                      <><CreditCard className="w-4 h-4 mr-2" /> Configurar Pagamento</>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </div>

            <div className="flex items-center justify-between">
              <Button variant="outline" onClick={() => setStep("details")}>
                Voltar
              </Button>
              <Button
                variant="ghost"
                onClick={() => setStep("channels")}
                className="text-muted-foreground"
              >
                Pular por agora <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Canais de Atendimento */}
        {step === "channels" && (
          <div>
            <div className="mb-8">
              <h1 className="text-4xl font-light text-foreground mb-2" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                Canais de Atendimento
              </h1>
              <p className="text-muted-foreground">
                Conecte seu WhatsApp ou Instagram para que o assistente de IA responda automaticamente.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* WhatsApp */}
              <Card
                className={`cursor-pointer transition-all duration-200 ${
                  channelRequested === "whatsapp"
                    ? "border-green-500 ring-2 ring-green-500/20 bg-green-50/50"
                    : "border-border hover:border-green-400/50"
                }`}
                onClick={() => setChannelRequested(channelRequested === "whatsapp" ? null : "whatsapp")}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-green-500/10 rounded-xl flex items-center justify-center">
                        <MessageCircle className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">WhatsApp</CardTitle>
                        <CardDescription>Resposta automática no WhatsApp</CardDescription>
                      </div>
                    </div>
                    {channelRequested === "whatsapp" && (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-1.5 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <Wifi className="w-3.5 h-3.5 text-green-500 shrink-0" />
                      Assistente responde 24/7 automaticamente
                    </li>
                    <li className="flex items-center gap-2">
                      <Wifi className="w-3.5 h-3.5 text-green-500 shrink-0" />
                      Agendamentos direto pelo WhatsApp
                    </li>
                    <li className="flex items-center gap-2">
                      <Wifi className="w-3.5 h-3.5 text-green-500 shrink-0" />
                      Usa o número do seu salão
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Instagram */}
              <Card
                className={`cursor-pointer transition-all duration-200 ${
                  channelRequested === "instagram"
                    ? "border-pink-500 ring-2 ring-pink-500/20 bg-pink-50/50"
                    : "border-border hover:border-pink-400/50"
                }`}
                onClick={() => setChannelRequested(channelRequested === "instagram" ? null : "instagram")}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-pink-500/10 rounded-xl flex items-center justify-center">
                        <Instagram className="w-6 h-6 text-pink-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">Instagram</CardTitle>
                        <CardDescription>Resposta automática nas DMs</CardDescription>
                      </div>
                    </div>
                    {channelRequested === "instagram" && (
                      <CheckCircle2 className="w-5 h-5 text-pink-500" />
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-1.5 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <Wifi className="w-3.5 h-3.5 text-pink-500 shrink-0" />
                      Responde mensagens diretas no Instagram
                    </li>
                    <li className="flex items-center gap-2">
                      <Wifi className="w-3.5 h-3.5 text-pink-500 shrink-0" />
                      Capta clientes das redes sociais
                    </li>
                    <li className="flex items-center gap-2">
                      <Wifi className="w-3.5 h-3.5 text-pink-500 shrink-0" />
                      Requer conta profissional
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Campo de telefone se WhatsApp selecionado */}
            {channelRequested === "whatsapp" && (
              <div className="mb-6 p-4 rounded-xl border border-green-200 bg-green-50/50">
                <Label htmlFor="channelPhone" className="text-sm font-medium mb-2 block">
                  Número do WhatsApp do salão (opcional)
                </Label>
                <div className="flex items-center gap-2 max-w-xs">
                  <Phone className="w-4 h-4 text-muted-foreground shrink-0" />
                  <Input
                    id="channelPhone"
                    placeholder="(11) 99999-9999"
                    value={channelPhone}
                    onChange={(e) => setChannelPhone(e.target.value)}
                    className="h-9"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Nossa equipe entrará em contato neste número para configurar a conexão.
                </p>
              </div>
            )}

            <div className="flex items-center justify-between">
              <Button variant="outline" onClick={() => setStep("payment")}>
                Voltar
              </Button>
              <div className="flex gap-3">
                {!channelRequested && (
                  <Button
                    variant="ghost"
                    onClick={() => setStep("agent")}
                    className="text-muted-foreground"
                  >
                    Pular por agora
                  </Button>
                )}
                <Button
                  onClick={handleChannelRequest}
                  disabled={requestChannel.isPending}
                  className={`px-8 ${channelRequested ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
                >
                  {requestChannel.isPending ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...</>
                  ) : channelRequested ? (
                    <>Próximo <ArrowRight className="ml-2 w-4 h-4" /></>
                  ) : (
                    <>Próximo <ArrowRight className="ml-2 w-4 h-4" /></>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Agente de IA */}
        {step === "agent" && (
          <div>
            <div className="mb-8">
              <h1 className="text-4xl font-light text-foreground mb-2" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                Agente de IA
              </h1>
              <p className="text-muted-foreground">
                Seu salão pode ter um assistente virtual que responde clientes, agenda e cancela — 24 horas por dia, 7 dias por semana.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {[
                {
                  icon: <MessageCircle className="w-6 h-6 text-green-500" />,
                  title: "Responde no WhatsApp",
                  desc: "O agente atende clientes automaticamente, sem precisar de ninguém online.",
                  color: "bg-green-50 border-green-200",
                },
                {
                  icon: <Calendar className="w-6 h-6 text-blue-500" />,
                  title: "Agenda e cancela",
                  desc: "Cria, confirma e cancela agendamentos diretamente pela conversa.",
                  color: "bg-blue-50 border-blue-200",
                },
                {
                  icon: <Bot className="w-6 h-6 text-amber-500" />,
                  title: "Personalidade própria",
                  desc: "Configure o nome, tom de voz e as regras do agente para combinar com seu salão.",
                  color: "bg-amber-50 border-amber-200",
                },
              ].map((feat, i) => (
                <div key={i} className={`p-4 rounded-xl border ${feat.color} space-y-2`}>
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                    {feat.icon}
                  </div>
                  <p className="font-semibold text-sm">{feat.title}</p>
                  <p className="text-xs text-muted-foreground">{feat.desc}</p>
                </div>
              ))}
            </div>

            <div className="p-5 rounded-xl border border-amber-200 bg-amber-50/60 mb-8">
              <div className="flex items-start gap-3">
                <Settings className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">Ativação pelo Super Admin</p>
                  <p className="text-xs text-amber-700 mt-1">
                    O agente de IA é configurado e ativado pela equipe da plataforma. Após concluir seu cadastro,
                    entre em contato para solicitar a ativação — normalmente feita em até 24 horas.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Button variant="outline" onClick={() => setStep("channels")}>
                Voltar
              </Button>
              <Button
                onClick={() => setStep("setup")}
                className="bg-primary text-primary-foreground px-8"
              >
                Próxima etapa <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 6: Configuração inicial guiada */}
        {step === "setup" && (() => {
          const setupItems = [
            {
              icon: <Scissors className="w-5 h-5 text-primary" />,
              title: "Serviços cadastrados",
              description: "Defina preço e duração para a agenda e o agente de IA.",
              completed: (onboardingServices?.length ?? 0) > 0,
              progress: `${onboardingServices?.length ?? 0} cadastrado${(onboardingServices?.length ?? 0) === 1 ? "" : "s"}`,
              cta: "Cadastrar serviços",
              path: "/services",
            },
            {
              icon: <Users className="w-5 h-5 text-blue-500" />,
              title: "Profissionais cadastrados",
              description: "Inclua a equipe que executará os atendimentos.",
              completed: (onboardingProfessionals?.length ?? 0) > 0,
              progress: `${onboardingProfessionals?.length ?? 0} cadastrado${(onboardingProfessionals?.length ?? 0) === 1 ? "" : "s"}`,
              cta: "Cadastrar profissionais",
              path: "/professionals",
            },
            {
              icon: <Calendar className="w-5 h-5 text-green-500" />,
              title: "Expediente configurado",
              description: "Informe os dias e horários reais de funcionamento da unidade.",
              completed: (onboardingSchedule ?? []).some((day: any) => day.isOpen),
              progress: (onboardingSchedule ?? []).some((day: any) => day.isOpen) ? "Horário informado" : "Pendente",
              cta: "Configurar horários",
              path: "/settings",
            },
            {
              icon: <MessageCircle className="w-5 h-5 text-emerald-500" />,
              title: "Canal de atendimento",
              description: "Acompanhe a solicitação de WhatsApp ou Instagram para ativar o agente.",
              completed: (onboardingChannels ?? []).some((request: any) => request.status === "done"),
              progress: (onboardingChannels ?? []).length ? "Solicitação registrada" : "Ainda não solicitado",
              cta: "Abrir integrações",
              path: "/integrations",
            },
          ];
          const completedItems = setupItems.filter((item) => item.completed).length;
          return (
            <div>
              <div className="mb-8">
                <div className="mb-3 flex items-center gap-2"><Settings className="h-5 w-5 text-primary" /><span className="text-sm font-semibold text-primary">Configuração inicial</span></div>
                <h1 className="text-4xl font-light text-foreground mb-2" style={{ fontFamily: "Cormorant Garamond, serif" }}>Deixe seu salão pronto para atender</h1>
                <p className="text-muted-foreground">Conclua as configurações essenciais. Você pode finalizar agora ou seguir para o painel e completar as pendências depois.</p>
              </div>

              <Card className="mb-5 border-primary/20 bg-primary/5"><CardContent className="pt-5"><div className="flex items-center justify-between gap-4"><div><p className="text-sm font-semibold">Progresso da configuração</p><p className="text-xs text-muted-foreground mt-1">{completedItems} de {setupItems.length} itens concluídos</p></div><Badge variant={completedItems === setupItems.length ? "default" : "secondary"}>{completedItems === setupItems.length ? "Pronto para operar" : "Em configuração"}</Badge></div><div className="mt-3 h-2 overflow-hidden rounded-full bg-primary/10"><div className="h-full rounded-full bg-primary transition-all" style={{ width: `${(completedItems / setupItems.length) * 100}%` }} /></div></CardContent></Card>

              <div className="space-y-3">
                {setupItems.map((item) => (
                  <Card key={item.title} className={item.completed ? "border-emerald-500/25 bg-emerald-500/[0.03]" : "border-border/70"}>
                    <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center">
                      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${item.completed ? "bg-emerald-500/10" : "bg-muted"}`}>{item.completed ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : item.icon}</div>
                      <div className="min-w-0 flex-1"><div className="flex flex-wrap items-center gap-2"><p className="text-sm font-semibold">{item.title}</p><Badge variant={item.completed ? "default" : "secondary"} className="text-[10px]">{item.completed ? "Concluído" : item.progress}</Badge></div><p className="mt-1 text-xs text-muted-foreground">{item.description}</p></div>
                      <Button size="sm" variant={item.completed ? "outline" : "default"} onClick={() => navigate(item.path)}>{item.completed ? "Revisar" : item.cta}<ChevronRight className="ml-1 h-3.5 w-3.5" /></Button>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="mt-8 flex items-center justify-between">
                <Button variant="outline" onClick={() => setStep("agent")}>Voltar</Button>
                <Button onClick={() => setStep("success")} className="bg-primary text-primary-foreground">{completedItems === setupItems.length ? "Concluir configuração" : "Ir para o painel e concluir depois"}<ArrowRight className="ml-2 h-4 w-4" /></Button>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
